import { useState, useEffect } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js'
import { apiService, ApiError } from '../services/api'
import { useDateRange } from '../context/DateRangeContext'
import { useNotification } from '../context/NotificationContext'
import { useLanguage } from '../context/LanguageContext'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

interface SegmentationData {
  date: string
  power_user: number
  regular: number
  low_activity: number
  non_adopter: number
}

export default function UserTypesChart() {
  const [data, setData] = useState<SegmentationData[]>([])
  const [loading, setLoading] = useState(true)
  const { startDate, endDate } = useDateRange()
  const { showNotification } = useNotification()
  const { t, language } = useLanguage()

  useEffect(() => {
    if (startDate && endDate) {
      loadData()
    }
  }, [startDate, endDate])

  const loadData = async () => {
    try {
      setLoading(true)
      const result = await apiService.getSegmentationTimeSeries(startDate, endDate)
      setData(result)
    } catch (error) {
      if (error instanceof ApiError) {
        const [title, ...messageParts] = error.userMessage.split(': ')
        showNotification('error', title, messageParts.join(': '))
      } else {
        showNotification(
          'error',
          t.errors.failedToLoadChartData,
          t.errors.tryRefreshing
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (isoString: string) => {
    const date = new Date(isoString)
    const locale = language === 'nl' ? 'nl-NL' : 'en-US'
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  const chartData = {
    labels: data.map(item => formatDate(item.date)),
    datasets: [
      {
        label: t.segments.powerUser,
        data: data.map(item => item.power_user),
        borderColor: '#FBB300',
        backgroundColor: 'rgba(251, 179, 0, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: data.length > 30 ? 0 : 3,
      },
      {
        label: t.segments.regular,
        data: data.map(item => item.regular),
        borderColor: '#5ABFEC',
        backgroundColor: 'rgba(90, 191, 236, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: data.length > 30 ? 0 : 3,
      },
      {
        label: t.segments.lowUsage,
        data: data.map(item => item.low_activity),
        borderColor: '#F29100',
        backgroundColor: 'rgba(242, 145, 0, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: data.length > 30 ? 0 : 3,
      },
      {
        label: t.segments.inactive,
        data: data.map(item => item.non_adopter),
        borderColor: '#959DA9',
        backgroundColor: 'rgba(149, 157, 169, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: data.length > 30 ? 0 : 3,
      },
    ],
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        beginAtZero: true,
        stacked: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        title: {
          display: true,
          text: t.userTypesChart.users,
          font: {
            weight: 'bold',
          },
        },
      },
    },
  }

  return (
    <div>
      {/* Chart */}
      {data.length > 0 ? (
        <div style={{ height: '350px' }}>
          <Line data={chartData} options={options} />
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">{t.timeChart.noDataAvailable}</p>
          <p className="text-sm mt-2">{t.timeChart.tryDifferentRange}</p>
        </div>
      )}
    </div>
  )
}
